import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { useLatestYearlyClosure } from 'utils/closureHooks';
import { MonthYearString } from 'types/MonthYearString';
import { useGetLatestStockModificationDate, useInitialInboundStockDate } from 'hooks';
import { checkDateIsStrictlyLower, getMonthAndYearStringsBetweenTwoDates } from 'utils/date';
import { ColorTypeEnum } from 'theme';
import { AppContext } from 'context';
import { InventoriedInboundStockForm } from './InventoriedInboundStockForm';
import { InboundStockCard } from './InboundStockCard';
import { UserRole } from '../../../types';

const useStyles = makeStyles(theme => ({
  typography: {
    display: 'flex',
    width: '100%',
  },
  element: {
    textTransform: 'capitalize',
    width: '25%',
  },
}));

const InboundStockList: React.FC = () => {
  const classes = useStyles();

  const [monthYearStrings, setMonthYearStrings] = useState<MonthYearString[]>([]);
  const [inventoriedStockDates, setInventoriedStockDates] = useState<MonthYearString[]>([]);
  const endDate = useGetLatestStockModificationDate();
  const [latestClosure] = useLatestYearlyClosure();

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));
  const sortingCenterInitializationDate = sortingCenter ? sortingCenter.initializationDate : null;

  const initialInboundStockDateString = useInitialInboundStockDate();

  useEffect(() => {
    if (!sortingCenterInitializationDate || !endDate) {
      return;
    }

    const initialInboundStockDate = initialInboundStockDateString ? new Date(initialInboundStockDateString) : null;
    const nextJanuary = new Date(new Date(sortingCenterInitializationDate).getFullYear() + 1, 0, 1);
    const shouldDisplayInitMonth =
      initialInboundStockDate && checkDateIsStrictlyLower(initialInboundStockDate, nextJanuary);
    const initializationYear = shouldDisplayInitMonth ? `${initialInboundStockDate.getFullYear()}` : null;
    const initializationMonth = shouldDisplayInitMonth ? `${initialInboundStockDate.getMonth() + 1}` : null;

    const newMonthYearStrings: MonthYearString[] = getMonthAndYearStringsBetweenTwoDates(
      shouldDisplayInitMonth ? initialInboundStockDate : sortingCenterInitializationDate,
      endDate ? new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1) : null,
      false,
    ).filter(
      monthYearString =>
        monthYearString.month === '1' ||
        (shouldDisplayInitMonth &&
          monthYearString.month === initializationMonth &&
          monthYearString.year === initializationYear),
    );

    setMonthYearStrings(newMonthYearStrings);

    setInventoriedStockDates(
      getMonthAndYearStringsBetweenTwoDates(
        endDate ? new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1) : initialInboundStockDate,
        new Date(),
        false,
      )
        .filter(monthYearString => monthYearString.month === '12')
        .map(monthYearString => ({
          month: '1',
          year: `${+monthYearString.year + 1}`,
          monthInLetter: 'Janvier',
        })),
    );
  }, [
    initialInboundStockDateString,
    endDate,
    sortingCenterInitializationDate,
    setMonthYearStrings,
    setInventoriedStockDates,
  ]);

  const isClosed = useCallback(
    (year: string): boolean => {
      if (!latestClosure) {
        if (!initialInboundStockDateString) return false;
        const initializationDate = new Date(initialInboundStockDateString);
        // Nous avons mis 11 car la méthode getMonth récupere les mois de 0 a 11.
        // A corrigé avec une autre librairie de date.
        return initializationDate.getFullYear() === Number(year) || initializationDate.getMonth() === 11;
      }
      return Number(year) <= latestClosure.year + 1;
    },
    [latestClosure, initialInboundStockDateString],
  );

  return (
    <>
      {inventoriedStockDates.map(monthYearString => (
        <CollapsableCard
          key={monthYearString.monthInLetter + monthYearString.year}
          color={ColorTypeEnum.WARNING}
          collapsableContent={<InventoriedInboundStockForm year={monthYearString.year} readOnly={readOnly} />}
        >
          <Typography className={classes.typography}>
            <span className={classes.element}>{monthYearString.monthInLetter}</span>
            <span className={classes.element}>{monthYearString.year}</span>
          </Typography>
        </CollapsableCard>
      ))}
      {monthYearStrings.map(monthYearString => (
        <InboundStockCard
          key={monthYearString.monthInLetter + monthYearString.year}
          monthYearString={monthYearString}
          isClosed={isClosed(monthYearString.year)}
          readOnly={readOnly}
        />
      ))}
    </>
  );
};
export default InboundStockList;
